import { Link } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { StatusBadge } from "@/components/vroom/status-badge";
import { fmtMinutes } from "./summary-rows.tsx";
import type { HoursEntriesAdminWeekResponse } from "@/api/generated/types.gen";

type Line = HoursEntriesAdminWeekResponse["groups"][number]["lines"][number];

export type RolledStatus = "to_check" | "checked" | "rejected" | "exported";
type StatusKind = "todo" | "ok" | "rej" | "exp";

export function toStatusKind(s: RolledStatus): StatusKind {
  switch (s) {
    case "to_check":
      return "todo";
    case "checked":
      return "ok";
    case "rejected":
      return "rej";
    case "exported":
      return "exp";
  }
}

interface LineRowProps {
  line: Line;
  weekDates: string[];
  workorders: HoursEntriesAdminWeekResponse["meta"]["related"]["workorders"];
  cranes: HoursEntriesAdminWeekResponse["meta"]["related"]["cranes"];
  groupKind: "worker" | "workorder-crane";
  groupKey: { workerName?: string; workerId?: string };
}

export function LineRow({
  line,
  weekDates,
  workorders,
  cranes,
  groupKind,
  groupKey,
}: LineRowProps) {
  const wo = workorders.find((w) => w.id === line.workorderId);
  const crane = cranes.find((c) => c.id === line.craneId);
  const minutesPerDay = weekDates.map((d) =>
    line.entries
      .filter((e) => e.date === d)
      .reduce(
        (a, e) =>
          a + Math.round((new Date(e.endAt).getTime() - new Date(e.startAt).getTime()) / 60_000),
        0,
      ),
  );
  const total = minutesPerDay.reduce((a, b) => a + b, 0);
  return (
    <tr className="border-b border-line text-xs">
      <td className="pl-6 py-1.5"></td>
      <td className="py-1.5">
        <StatusBadge status={toStatusKind(line.status)} />
        {groupKind === "worker" ? (wo?.number ?? "—") : (groupKey.workerName ?? "—")}
      </td>
      <td className="py-1.5">{wo?.city ?? ""}</td>
      <td className="py-1.5">
        {line.hasComment && <MessageSquare size={12} aria-label="Opmerking" />}
      </td>
      <td className="py-1.5">{crane?.number ?? ""}</td>
      {minutesPerDay.map((m, i) => (
        <td key={i} className="tabular-nums text-right pr-2 py-1.5">
          {m > 0 ? (
            <Link
              to="/admin/uren/$entryId/edit"
              params={{ entryId: line.entries.find((e) => e.date === weekDates[i])?.id ?? "" }}
              className="text-ink-700 hover:underline"
            >
              {fmtMinutes(m)}
            </Link>
          ) : (
            "–"
          )}
        </td>
      ))}
      <td className="tabular-nums text-right pr-3 py-1.5 font-semibold">{fmtMinutes(total)}</td>
    </tr>
  );
}
